import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { TooltipLayer } from '@/shared/components/ui/TooltipLayer';
import { useUserPreferences } from '@/features/auth/preferences';
import { IncomingCallOverlay } from '@/features/atendimento/voip/IncomingCallOverlay';
import { CallSessionProvider } from '@/features/atendimento/voip/call-session';
import { useIncomingCallListener } from '@/features/atendimento/voip/useIncomingCallListener';
import { useDesktopNotifications } from '@/features/notifications/hooks/useDesktopNotifications';
import { useTituloDaAba } from '@/features/notifications/hooks/useTituloDaAba';
import { onLogoutBroadcast } from '@/shared/lib/auth-broadcast';
import { clearPersistedQueryCache } from '@/shared/lib/query-cache';
import { useThemeStore } from '@/shared/stores/theme-store';
import { type ReactNode, useEffect } from 'react';
import { SupportBanner } from './SupportBanner';
import { Topbar } from './Topbar';

export function AppShell({ children }: { children: ReactNode }) {
  useDesktopNotifications();
  // "(3) ChatPlus" na aba: atendimentos ativos com mensagem não lida.
  useTituloDaAba();
  // Chamada entrante (VoIP) toca em qualquer rota — listener global.
  useIncomingCallListener();

  // Logout entre abas do mesmo browser: quando uma aba clica "Sair" (logout
  // per-device que NÃO emite socket), avisa as outras via BroadcastChannel pra
  // caírem na hora — sem esperar o F5. Mesmo browser compartilha a sessão.
  useEffect(
    () =>
      onLogoutBroadcast(() => {
        clearPersistedQueryCache();
        window.location.href = '/login';
      }),
    [],
  );

  // Banco = fonte da verdade do tema (cross-device). O localStorage é só cache
  // de anti-flash. No boot, quando as prefs carregam, sincronizamos:
  //   - DB tem theme → aplica (logar em outro PC abre no tema escolhido).
  //   - DB sem theme → claro (default). Isso reverte quem ficou no escuro só por
  //     localStorage/sistema, sem registro no banco.
  const prefs = useUserPreferences().data;
  const setPref = useThemeStore((s) => s.setPref);
  useEffect(() => {
    if (!prefs) return;
    const target = prefs.theme ?? 'light';
    if (target !== useThemeStore.getState().resolved) {
      setPref(target);
    }
  }, [prefs, setPref]);

  return (
    // Sessão da chamada de voz acima das rotas: a ligação segue no ar ao
    // navegar e a janelinha flutuante aparece em qualquer tela.
    <CallSessionProvider>
      <div className="flex h-screen w-screen flex-col overflow-hidden bg-n-50">
        <SupportBanner />
        <Topbar />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <main className="flex min-h-0 flex-1 overflow-hidden">{children}</main>
        </div>
        {/* Toaster vive em main.tsx — fora de AppShell pra ficar montado também
          em /login (auth toasts) e durante bootstrap auth. */}
        <ConfirmDialog />
        <TooltipLayer />
        <IncomingCallOverlay />
      </div>
    </CallSessionProvider>
  );
}
